import { SettingUrls } from 'constants/SettingsUrls';
import { PackageProviderName, WizardStepName } from 'data/ppoints/constants';
import { useShippingProviderCountries } from 'features/shipping-provider/hooks/useShippingProviderCountries';
import {
    ProviderMarketRateSource,
    useMarketRateCountryGroups,
} from 'features/shipping-provider/settings/hooks/useMarketRateCountryGroups';
import { ShippingRateMappingForm } from 'features/shipping-provider/settings/hooks/useProviderRateMappingData';
import WizardStepItemDone from 'features/wizard/components/WizardStepItemDone';
import useReportStepLoading from 'features/wizard/hooks/useReportStepLoading';
import useWizard from 'features/wizard/hooks/useWizard';
import { WizardStepContentProps } from 'features/wizard/models/WizardStepContentProps';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import useContextualSaveBar from 'hooks/useContextualSaveBar';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'redux/hooks';
import { getEnabledShippingTypes } from 'redux/slices/enabled-shipping-types/enabled-shipping-types.actions';
import MarketRateCountryCard from './MarketRateCountryCard';
import MarketRateCountryCardSkeleton from './MarketRateCountryCardSkeleton';
import { MarketRateStepProviderData, MarketRateStepProviderSection } from './MarketRateStepProviderSection';
import MarketRateWithoutMarketBanner from './MarketRateWithoutMarketBanner';

const SAVE_BAR_ID = 'wizard-market-rate-savebar';

export interface MarketRateGroupForm {
    providers: Partial<Record<PackageProviderName, ShippingRateMappingForm>>;
}

/**
 * Market-driven-shipping counterpart to `ShippingZoneRateStep` — same step shell (isDone check,
 * loading state, intro copy, "Next" button, wizard step tracked under the same
 * `WizardStepName.shipping_zone_rate`) and the same one-card-per-country-listing-every-provider
 * layout `ShippingZoneRateStep` gets from grouping by zone, but backed by
 * `useProviderRateMappingData`'s per-country rate mapping instead of `useZonesWithMappedProviders`'
 * per-zone one.
 *
 * That data hook is per-provider (the countries it returns are provider-specific), so a headless
 * `MarketRateStepProviderSection` calls it once per active provider — all pointed at ONE shared form
 * and ONE save bar owned here (only one save bar can be shown at a time) — and reports its result up.
 * `useMarketRateCountryGroups` then regroups every provider's report by country, and this step
 * renders from that grouping instead of from the per-provider reports directly.
 */
export const MarketRateStep: React.FC<WizardStepContentProps> = ({ onLoadingChange }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const wizard = useWizard();
    const dispatch = useAppDispatch();
    const { setBanner } = useErrorBanner();
    const isDone = wizard.isStepDone(WizardStepName.shipping_zone_rate);

    const { providerToCountry, isLoading: isLoadingProviderCountries } = useShippingProviderCountries();

    const activeProviderNames = useMemo(
        () => [...new Set(Object.values(providerToCountry ?? {}).flat())],
        [providerToCountry]
    );

    const [providerData, setProviderData] = useState<Partial<Record<PackageProviderName, MarketRateStepProviderData>>>(
        {}
    );

    // Belt-and-suspenders against the reporting child re-firing on a render that didn't really change
    // anything (see `MarketRateStepProviderSection`'s `countriesSignature`): skip the state update —
    // and with it the re-render this component would otherwise cause right back in that child — when
    // the incoming report is a no-op next to what's already stored for this provider.
    const handleDataChange = useCallback((provider: PackageProviderName, data: MarketRateStepProviderData) => {
        setProviderData((prev) => {
            const previous = prev[provider];

            if (
                previous &&
                previous.defaultsSignature === data.defaultsSignature &&
                previous.valuesSignature === data.valuesSignature &&
                previous.isInitialLoading === data.isInitialLoading &&
                previous.error?.message === data.error?.message &&
                previous.optionsError?.message === data.optionsError?.message &&
                previous.withMarket.length === data.withMarket.length &&
                previous.withoutMarket.length === data.withoutMarket.length
            ) {
                return prev;
            }

            return { ...prev, [provider]: data };
        });
    }, []);

    const form = useForm<MarketRateGroupForm>({ defaultValues: { providers: {} } });

    // Presence alone isn't enough — a provider's first report can still have `isInitialLoading: true`
    // (its own mapping/options request not settled yet), and treating that as "loaded" let `isLoading`
    // flip false — and the without-market banner render from incomplete `withMarket`/`withoutMarket`
    // data — for a moment before every provider actually finished.
    const allProvidersLoaded =
        activeProviderNames.length > 0 &&
        activeProviderNames.every((provider) => providerData[provider] && !providerData[provider].isInitialLoading);

    const hasError = activeProviderNames.some((provider) => providerData[provider]?.error);

    // Content-keyed so a background revalidation doesn't reset the form (and lose unsaved edits)
    // unless a provider's actual defaults changed — same reasoning as the single-provider hook this
    // is built on. Waits for every active provider to report in before resetting at all, so an
    // earlier provider's arrival can't reset over a later provider's still-loading (empty) slice.
    const defaultsSignature = allProvidersLoaded
        ? activeProviderNames.map((provider) => providerData[provider].defaultsSignature).join('|')
        : undefined;

    // Only the content-keyed signature should re-trigger the reset — `providerData`/`form` are
    // deliberately left out of the dependency list, same reasoning as the single-provider hook.
    useEffect(() => {
        if (!defaultsSignature) return;

        form.reset({
            providers: Object.fromEntries(
                activeProviderNames.map((provider) => [provider, providerData[provider].defaults])
            ),
        });
    }, [defaultsSignature]);

    const sources: ProviderMarketRateSource[] = useMemo(
        () =>
            allProvidersLoaded && !hasError
                ? activeProviderNames.map((provider) => ({ provider, ...providerData[provider] }))
                : [],
        [allProvidersLoaded, hasError, activeProviderNames, providerData]
    );

    const countryGroups = useMarketRateCountryGroups(sources);

    const { withMarketGroups, withoutMarketCountryCodes } = useMemo(() => {
        const groups = [...countryGroups.values()];

        return {
            withMarketGroups: groups.filter((group) => group.hasMarket),
            withoutMarketCountryCodes: groups.filter((group) => !group.hasMarket).map((group) => group.countryCode),
        };
    }, [countryGroups]);

    const mutateAllProviders = useCallback(() => {
        activeProviderNames.forEach((provider) => providerData[provider]?.mutate());
    }, [activeProviderNames, providerData]);

    // A new market brings its own shipping rates, which the rate options only pick up on a refetch of
    // their own — `mutate` alone revalidates the mappings and markets.
    const handleMarketCreated = useCallback(() => {
        activeProviderNames.forEach((provider) => {
            providerData[provider]?.mutate();
            providerData[provider]?.mutateOptions();
        });
    }, [activeProviderNames, providerData]);

    const onSubmit = useCallback(
        async (data: MarketRateGroupForm) => {
            try {
                await Promise.all(
                    activeProviderNames.map((provider) =>
                        providerData[provider].save(data.providers[provider] ?? providerData[provider].defaults)
                    )
                );
            } catch (error) {
                console.error('Failed to save market rate mappings', error);
                setBanner({
                    titleKey: 'SHIPPING_PROVIDERS.API_SETTNGS.ERROR_BANNER.SETTINGS_ERROR.TITLE',
                    descriptionKey: 'SHIPPING_PROVIDERS.API_SETTNGS.ERROR_BANNER.SETTINGS_ERROR.DESCRIPTION',
                    tone: 'warning',
                    withSupportActions: true,
                });
                return;
            }

            form.reset(data);
            shopify.toast.show(t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.TOAST.SAVED'));

            mutateAllProviders();
            dispatch(getEnabledShippingTypes());

            await wizard.handleWizardStepper(WizardStepName.shipping_zone_rate);
        },
        [activeProviderNames, providerData, form.reset, setBanner, mutateAllProviders, dispatch, t, wizard]
    );

    const { saveBar } = useContextualSaveBar<MarketRateGroupForm>(form, onSubmit, SAVE_BAR_ID, false);

    const isLoading = useMemo(() => {
        if (isDone) return false;
        return isLoadingProviderCountries || !allProvidersLoaded;
    }, [isDone, isLoadingProviderCountries, allProvidersLoaded]);

    useReportStepLoading(isLoading, onLoadingChange);

    const handleNext = useCallback(async () => {
        if (form.formState.isDirty) {
            form.handleSubmit(onSubmit)();
            return;
        }
        await wizard.handleWizardStepper(WizardStepName.shipping_zone_rate);
    }, [form, onSubmit, wizard]);

    if (isDone) {
        return (
            <WizardStepItemDone
                message={t('WIZARD.MARKET_DRIVEN_SHIPPING_STEP.DESCRIPTION')}
                action={
                    <s-button onClick={() => navigate(SettingUrls.shippingProviders)}>
                        {t('WIZARD.MARKET_DRIVEN_SHIPPING_STEP.MODIFY_SETTINGS')}
                    </s-button>
                }
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {saveBar}
            <s-text>{t('WIZARD.MARKET_DRIVEN_SHIPPING_STEP.INTRO')}</s-text>

            {/* Headless — keeps fetching every active provider's data even while the skeleton below
            is shown, so loading can actually finish. */}
            {activeProviderNames.map((provider) => (
                <MarketRateStepProviderSection
                    key={provider}
                    provider={provider}
                    form={form}
                    onDataChange={handleDataChange}
                />
            ))}

            {isLoading ? (
                <MarketRateCountryCardSkeleton />
            ) : (
                <>
                    {activeProviderNames.length === 0 && (
                        <s-text color="subdued">{t('WIZARD.SHIPPING_ZONE_RATE_STEP.EMPTY')}</s-text>
                    )}

                    {hasError && (
                        <s-banner
                            tone="warning"
                            heading={t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.ERROR_BANNER.TITLE')}
                        >
                            <s-paragraph>
                                {t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.ERROR_BANNER.DESCRIPTION')}
                            </s-paragraph>
                        </s-banner>
                    )}

                    {withMarketGroups.map((group) => (
                        <MarketRateCountryCard key={group.countryCode} group={group} />
                    ))}

                    {!withMarketGroups.length && !!withoutMarketCountryCodes.length && (
                        <MarketRateWithoutMarketBanner
                            countryCodes={withoutMarketCountryCodes}
                            onMarketCreated={handleMarketCreated}
                        />
                    )}

                    {activeProviderNames.length > 0 && (
                        <div className="flex justify-end pt-1">
                            <s-button variant="primary" loading={form.formState.isSubmitting} onClick={handleNext}>
                                {t('WIZARD.STEP_NEXT_BUTTON')}
                            </s-button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
